import './PageToolbar.scss';

import BackButton from 'components/BackButton/BackButton';
import type { PropsWithChildren, ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

export interface PageHeaderDefinition {
  title: ReactNode;
  actions?: ReactNode;
  status?: ReactNode;
  backUrl?: string;
  backLabel?: string;
}

interface ToolbarDestination {
  placement: 'header' | 'content';
  target: HTMLDivElement | null;
}

const DestinationContext = createContext<ToolbarDestination | undefined>(undefined);
const TargetContext = createContext<((node: HTMLDivElement | null) => void) | undefined>(undefined);

/** Only the destination is shared. Page actions remain owned by their routed page. */
export const PageToolbarProvider = ({
  placement,
  children
}: PropsWithChildren<{
  placement: ToolbarDestination['placement'];
}>) => {
  const [target, setTarget] = useState<HTMLDivElement | null>(null);
  const destination = useMemo(() => ({ placement, target }), [placement, target]);
  return (
    <TargetContext.Provider value={setTarget}>
      <DestinationContext.Provider value={destination}>{children}</DestinationContext.Provider>
    </TargetContext.Provider>
  );
};

export const PageToolbarSlot = () => {
  const setTarget = useContext(TargetContext);
  return <div className="Header_pageToolbarSlot" ref={setTarget} />;
};

/** One page-level toolbar per route; section headings and modal actions stay local. */
const PageToolbar = ({ title, actions, status, backUrl, backLabel }: PageHeaderDefinition) => {
  const destination = useContext(DestinationContext);
  const toolbar = (
    <div className="PageToolbar">
      <div className="PageToolbar_identity">
        {backUrl && <BackButton backUrl={backUrl} backLabel={backLabel} />}
        <h1 className="PageToolbar_title">{title}</h1>
        {status}
      </div>
      {actions && <div className="PageToolbar_actions">{actions}</div>}
    </div>
  );
  if (!destination || destination.placement === 'content') return toolbar;
  // Do not briefly render in content while the header callback ref is connecting.
  return destination.target ? createPortal(toolbar, destination.target) : null;
};

export default PageToolbar;
